import { AnyAction, createSelector, createSlice, Slice } from '@reduxjs/toolkit';
import type { TAppDispatch, TState } from '~/src/store';
import { startLoading, stopLoading } from '~/src/store/slices/appSlice';
import type { IProject } from '~/src/services/projects';
import { getProjectList, createProject, deleteProject } from '~/src/services/projects/projectApi';
import { EFilterOptions } from './projectFilterSlice';

interface IProjectSlice {
    list: IProject[];
    isError: boolean;
}

const projectsSlice: Slice<IProjectSlice> = createSlice({
    name: 'projects',
    initialState: {
        list: [],
        isError: false,
    },
    reducers: {
        clone: (state: IProjectSlice, action: AnyAction) => {
            state.list = action.payload;
        },
        create: (state: IProjectSlice, action: AnyAction) => {
            state.list = action.payload;
        },
        remove: (state: IProjectSlice, action: AnyAction) => {
            state.list = action.payload;
        },
        getAll: (state: IProjectSlice, action: AnyAction) => {
            state.list = action.payload;
        },
        error: (state: IProjectSlice, action: AnyAction) => {
            state.isError = !!action.payload;
        },
    },
});
const { actions } = projectsSlice;

export const getAll = () => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        dispatch(startLoading());
        const { auth: { tokenInfo: { token } } } = getState();

        const resp = await getProjectList(token);
        if (resp.isSuccess === true) {
            dispatch(actions.getAll(resp.payload));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const create = (project: Partial<IProject>) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        dispatch(startLoading());
        const { auth: { tokenInfo: { token } }, projects: { list } } = getState();

        const resp = await createProject(token, project);
        if (resp.isSuccess === true) {
            dispatch(actions.create([...list, resp.payload]));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const remove = (projectId: string) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        dispatch(startLoading());
        const { auth: { tokenInfo: { token } }, projects: { list } } = getState();

        const resp = await deleteProject(token, projectId);
        if (resp.isSuccess === true) {
            dispatch(actions.remove(list.filter(item => item._id !== projectId)));
        } else {
            dispatch(actions.error(resp.error));
        }

        dispatch(stopLoading());
    };
};

export const clone = (title: string, url:string) => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        dispatch(startLoading());

        const { currentProject: { project }, auth: { tokenInfo: { token } }, projects: { list } } = getState();
        const clonedProject = {
            ...project,
            _id: null,
            publicVersionId: null,
            isActive: false,
            title,
            url,
        };

        const createResp = await createProject(token, clonedProject);
        if (createResp.isSuccess === true) {
            dispatch(actions.clone([...list, createResp.payload]));
        } else {
            dispatch(actions.error(createResp.error));
        }

        dispatch(stopLoading());
    };
};

export const selectProjects = (state: TState): IProjectSlice => state.projects;

export const selectFilteredProjects = createSelector(
    selectProjects,
    (state: TState) => state.projectFilter.selectedOption,
    (projects, selectedOption) => {
      const { list } = projects;

      return list.filter((project) => {
        switch (selectedOption) {
          case EFilterOptions.active:
            return project.publicVersionId;
          case EFilterOptions.unactive:
            return !project.publicVersionId;
          default:
            return EFilterOptions.all;
        }
      });
    },
  );

export default projectsSlice.reducer;
